<template>
  <app-page labelledby="bill-card-title" full-width>
    <page-header
      title-id="bill-card-title"
      :title="cardTitle"
      description="Проводки, обороты и сальдо по выбранному счету."
    >
      <template #metadata>
        <div class="card-context">
          <span><strong>Счет:</strong> {{ bill.full_name || 'загружается' }}</span>
          <span><strong>Период:</strong> {{ periodLabel }}</span>
        </div>
      </template>
      <template #actions>
        <q-btn flat no-caps icon="arrow_back" label="К выбору счета" @click="goBack" />
        <date-range-filter v-model="period" label="Период" @apply="applyPeriod" />
        <report-artifact-actions
          :payload="artifactPayload"
          :org-id="connection.org_id"
          :disabled="loading || Boolean(entityState)"
          neutral
        />
        <q-btn
          flat
          no-caps
          icon="download"
          label="CSV"
          :disable="!filteredRows.length"
          data-testid="bill-card-export"
          @click="exportRows"
        />
      </template>
    </page-header>

    <app-state
      v-if="loading"
      state="loading"
      title="Загружаем карточку счета"
      message="Получаем проводки и сальдо."
      data-testid="bill-card-loading"
    />
    <app-state
      v-else-if="entityState"
      :state="entityState"
      :title="entityState === 'not-found' ? 'Счет не найден' : 'Не удалось загрузить карточку счета'"
      :message="entityMessage"
      :action-label="entityState === 'not-found' ? 'К выбору счета' : 'Повторить'"
      @action="entityState === 'not-found' ? goToSelector() : load()"
      data-testid="bill-card-state"
    />
    <template v-else>
      <page-section
        title="Движения и сальдо"
        description="Начальное сальдо, проводки, обороты и конечное сальдо в едином регистре."
      >
        <app-data-table
          title="Карточка счета"
          :count="filteredRows.length"
          :rows="filteredRows"
          :columns="columns"
          :pagination="pagination"
          :show-count="false"
          :rows-per-page-options="[100]"
          :table-style="{ width: '100%', minWidth: '0', tableLayout: 'fixed' }"
          wrap-cells
          :state="tableState"
          state-title="За период нет проводок"
          state-message="За выбранный период нет движений."
          storage-key="finance-bill-card"
          class="bill-card-register"
          data-testid="bill-card-table"
          @reset="search = ''"
        >
          <template #filters>
            <filter-bar
              v-model="search"
              search-label="Документ, операция или счет"
              search-test-id="bill-card-search"
              :has-filters="Boolean(search)"
              @reset="search = ''"
            />
          </template>
          <template #no-data>
            <div class="bill-card-no-data" role="status">
              За период нет проводок. Начальное и конечное сальдо показаны в таблице.
            </div>
          </template>
          <template #header="props">
            <q-tr :props="props" class="bill-card-header bill-card-header--groups">
              <q-th colspan="3" aria-label="Реквизиты проводки" />
              <q-th colspan="2" scope="colgroup">Дебет</q-th>
              <q-th colspan="2" scope="colgroup">Кредит</q-th>
              <q-th rowspan="2" scope="col">Текущее сальдо</q-th>
            </q-tr>
            <q-tr :props="props" class="bill-card-header bill-card-header--columns">
              <q-th scope="col" class="text-left">Дата</q-th>
              <q-th scope="col" class="text-left">Документ</q-th>
              <q-th scope="col" class="text-left">Операция</q-th>
              <q-th scope="col" class="text-left">Счет</q-th>
              <q-th scope="col">Сумма</q-th>
              <q-th scope="col">Сумма</q-th>
              <q-th scope="col" class="text-left">Счет</q-th>
            </q-tr>
            <q-tr class="bill-card-opening" data-testid="bill-card-opening">
              <q-th colspan="3" scope="row" class="text-left">Сальдо на начало</q-th>
              <q-th colspan="2" class="text-right">
                <amount-cell :value="summary.saldo_debet_start" />
                <small v-if="shouldShowCount">Количество: {{ displayValue(summary.saldo_debet_start_count) }}</small>
              </q-th>
              <q-th colspan="2" class="text-right">
                <amount-cell :value="summary.saldo_credit_start" />
                <small v-if="shouldShowCount">Количество: {{ displayValue(summary.saldo_credit_start_count) }}</small>
              </q-th>
              <q-th />
            </q-tr>
          </template>
          <template #body="props">
            <q-tr :props="props" :data-testid="`bill-card-row-${props.row.id}`">
              <q-td class="text-left bill-card-date">{{ props.row.date || '—' }}</q-td>
              <q-td class="text-left bill-card-document">
                <q-btn
                  v-if="hasSource(props.row)"
                  flat
                  dense
                  no-caps
                  class="bill-card-source"
                  :label="props.row.document || 'Без документа'"
                  :to="{ name: 'bill_operation_info', params: { id: props.row.id } }"
                  :data-testid="`bill-card-source-${props.row.id}`"
                />
                <span v-else>{{ props.row.document || 'Без документа' }}</span>
              </q-td>
              <q-td class="text-left bill-card-operation">{{ props.row.name || 'Операция' }}</q-td>
              <q-td class="text-left bill-card-account">{{ props.row.bill_to || '—' }}</q-td>
              <q-td class="text-right" :data-testid="`bill-card-debit-${props.row.id}`">
                <amount-cell :value="amountValue(props.row.bill_to_saldo)" />
                <small v-if="shouldShowMovementCount(props.row.bill_to_saldo)">
                  Количество: {{ displayValue(props.row.count) }}
                </small>
              </q-td>
              <q-td class="text-right" :data-testid="`bill-card-credit-${props.row.id}`">
                <amount-cell :value="amountValue(props.row.bill_from_saldo)" />
                <small v-if="shouldShowMovementCount(props.row.bill_from_saldo)">
                  Количество: {{ displayValue(props.row.count) }}
                </small>
              </q-td>
              <q-td class="text-left bill-card-account">{{ props.row.bill_from || '—' }}</q-td>
              <q-td class="text-right" :data-testid="`bill-card-current-${props.row.id}`">
                <amount-cell :value="amountValue(props.row.current_saldo)" />
                <small v-if="shouldShowCount">Количество: {{ displayValue(props.row.current_saldo_count) }}</small>
              </q-td>
            </q-tr>
          </template>
          <template #bottom-row>
            <q-tr class="bill-card-total-row" data-testid="bill-card-turnover">
              <q-td colspan="3" class="text-left text-bold">Обороты за период</q-td>
              <q-td />
              <q-td class="text-right text-bold"><amount-cell :value="summary.debet_end" /></q-td>
              <q-td class="text-right text-bold"><amount-cell :value="summary.credit_end" /></q-td>
              <q-td />
              <q-td />
            </q-tr>
            <q-tr class="bill-card-total-row bill-card-total-row--closing" data-testid="bill-card-closing">
              <q-td colspan="3" class="text-left text-bold">Сальдо на конец</q-td>
              <q-td colspan="2" class="text-right text-bold">
                <amount-cell :value="summary.saldo_debet_end" />
                <small v-if="shouldShowCount">Количество: {{ displayValue(summary.saldo_debet_end_count) }}</small>
              </q-td>
              <q-td colspan="2" class="text-right text-bold">
                <amount-cell :value="summary.saldo_credit_end" />
                <small v-if="shouldShowCount">Количество: {{ displayValue(summary.saldo_credit_end_count) }}</small>
              </q-td>
              <q-td />
            </q-tr>
          </template>
        </app-data-table>
      </page-section>
    </template>
  </app-page>
</template>

<script>
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import AmountCell from '../../../components/ui/AmountCell.vue'
import AppPage from '../../../components/ui/AppPage.vue'
import AppState from '../../../components/ui/AppState.vue'
import DateRangeFilter from '../../../components/ui/DateRangeFilter.vue'
import FilterBar from '../../../components/ui/FilterBar.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import PageSection from '../../../components/ui/PageSection.vue'
import ReportArtifactActions from '../../../components/documents/ReportArtifactActions.vue'
import { EMPTY_CARD_SUMMARY, downloadCsv } from '../../../services/financeCore'
import { formatDate, toIsoDate } from '../../../services/dateFormat'

const columns = Object.freeze([
  { name: 'date', label: 'Дата', field: 'date', align: 'left', required: true, sortable: true },
  { name: 'document', label: 'Документ', field: 'document', align: 'left', required: true },
  { name: 'operation', label: 'Операция', field: 'name', align: 'left', required: true },
  { name: 'bill_to', label: 'Счет', field: 'bill_to', align: 'left', required: true },
  { name: 'bill_to_saldo', label: 'Сумма', field: 'bill_to_saldo', align: 'right', required: true },
  { name: 'bill_from_saldo', label: 'Сумма', field: 'bill_from_saldo', align: 'right', required: true },
  { name: 'bill_from', label: 'Счет', field: 'bill_from', align: 'left', required: true },
  { name: 'current_saldo', label: 'Текущее сальдо', field: 'current_saldo', align: 'right', required: true }
])

export default {
  name: 'BillCard',
  components: { AppDataTable, AmountCell, AppPage, AppState, DateRangeFilter, FilterBar, PageHeader, PageSection, ReportArtifactActions },
  data() {
    const stored = this.$store.state.filter_period
    return {
      columns,
      bill: {},
      rows: [],
      summary: { ...EMPTY_CARD_SUMMARY },
      shouldShowCount: false,
      loading: true,
      entityState: '',
      entityMessage: '',
      search: '',
      period: { from: toIsoDate(stored.from), to: toIsoDate(stored.to) },
      pagination: { page: 1, rowsPerPage: 100, sortBy: 'date', descending: false }
    }
  },
  computed: {
    cardTitle() {
      return this.bill.full_name ? `Карточка счета ${this.bill.full_name}` : 'Карточка счета'
    },
    periodLabel() {
      return `${formatDate(this.period.from, '—')} — ${formatDate(this.period.to, '—')}`
    },
    artifactPayload() {
      return {
        org_id: this.connection.org_id,
        report_code: 'account_card',
        period_start: this.period.from,
        period_end: this.period.to,
        date: this.period.to,
        bill_id: this.$route.params.id,
        unit: 'rub',
        currency: 'RUB'
      }
    },
    filteredRows() {
      const query = this.search.trim().toLocaleLowerCase('ru-RU')
      if (!query) return this.rows
      return this.rows.filter((row) => [row.name, row.document, row.bill_to, row.bill_from]
        .some((value) => String(value || '').toLocaleLowerCase('ru-RU').includes(query)))
    },
    tableState() {
      if (this.rows.length && !this.filteredRows.length) return 'filtered-empty'
      return 'ready'
    }
  },
  created() {
    this.emitter.on('didReceiveTransfersListByBillCard', this.onCard)
    this.emitter.on('onBillCardError', this.onError)
    this.load()
  },
  beforeUnmount() {
    this.emitter.off('didReceiveTransfersListByBillCard', this.onCard)
    this.emitter.off('onBillCardError', this.onError)
  },
  methods: {
    load() {
      const billId = this.$route.params.id
      if (!billId) {
        this.loading = false
        this.entityState = 'not-found'
        this.entityMessage = 'В адресе не указан счет.'
        return
      }
      this.loading = true
      this.entityState = ''
      this.entityMessage = ''
      const request = () => this.connection.getBillCardTransfers('bill-card', billId, this.period.from, this.period.to)
      if (this.connection.isAuthenticated) request()
      else {
        const ready = () => {
          this.emitter.off('connectionReady', ready)
          request()
        }
        this.emitter.on('connectionReady', ready)
      }
    },
    onCard(response) {
      const data = response.data
      this.loading = false
      this.bill = data.bill
      this.rows = data.transfers
      this.summary = { ...EMPTY_CARD_SUMMARY, ...data.summary }
      this.shouldShowCount = data.should_show_count === true
    },
    onError(error) {
      this.loading = false
      this.entityState = error.state || 'error'
      this.entityMessage = error.message || 'Попробуйте повторить запрос.'
      this.rows = []
    },
    applyPeriod(value) {
      this.period = { ...value }
      this.$store.commit('updateFilterPeriod', value)
      this.load()
    },
    displayValue(value) {
      return value === undefined || value === null || value === '' ? '—' : value
    },
    amountValue(value) {
      return value === undefined || value === null || value === '' ? null : value
    },
    shouldShowMovementCount(value) {
      const amount = Number(value)
      return this.shouldShowCount && Number.isFinite(amount) && amount !== 0
    },
    hasSource(row) {
      return row && row.id !== undefined && row.id !== null && row.id !== ''
    },
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.goToSelector()
    },
    goToSelector() {
      this.$router.push({ name: 'account_cards_list' })
    },
    exportRows() {
      downloadCsv(`account-card-${this.bill.name || this.bill.id || 'data'}.csv`, [
        { name: 'date', label: 'Дата' },
        { name: 'document', label: 'Документ' },
        { name: 'name', label: 'Операция' },
        { name: 'bill_to', label: 'Счет Дт' },
        { name: 'bill_to_saldo', label: 'Сумма Дт' },
        { name: 'bill_from_saldo', label: 'Сумма Кт' },
        { name: 'bill_from', label: 'Счет Кт' },
        { name: 'current_saldo', label: 'Текущее сальдо' }
      ], this.filteredRows)
    }
  }
}
</script>

<style scoped>
.card-context { display: flex; flex-wrap: wrap; gap: var(--sid-space-4); }
.bill-card-register :deep(.q-table) { width: 100%; min-width: 0; table-layout: fixed; }
.bill-card-register :deep(.q-table th), .bill-card-register :deep(.q-table td) { padding-right: var(--sid-space-2); padding-left: var(--sid-space-2); }
.bill-card-header th, .bill-card-opening th, .bill-card-total-row td { border-color: var(--sid-color-line-strong); }
.bill-card-header--groups th { top: 0 !important; text-align: center; }
.bill-card-header--columns th { top: var(--sid-row-compact) !important; text-align: center; }
.bill-card-header--columns th.text-left { text-align: left; }
.bill-card-opening th { top: calc(var(--sid-row-compact) * 2) !important; }
.bill-card-opening th:first-child { text-align: left; }
.bill-card-opening small, .bill-card-total-row small, .bill-card-register :deep(tbody small) { display: block; color: var(--sid-color-text-secondary); font-weight: var(--sid-font-weight-regular); white-space: nowrap; }
.bill-card-header--columns th:nth-child(1), .bill-card-date { width: 5rem; white-space: nowrap; }
.bill-card-header--columns th:nth-child(2), .bill-card-document { width: 8rem; overflow-wrap: anywhere; }
.bill-card-header--columns th:nth-child(3), .bill-card-operation { width: 8rem; overflow-wrap: anywhere; }
.bill-card-header--columns th:nth-child(4), .bill-card-header--columns th:nth-child(7), .bill-card-account { width: 5rem; overflow-wrap: anywhere; }
.bill-card-header--columns th:nth-child(5), .bill-card-header--columns th:nth-child(6) { width: 6rem; }
.bill-card-header--groups th:last-child { width: 7rem; }
.bill-card-source { width: 100%; min-width: 0; max-width: 100%; min-height: 0; padding: 0; text-align: left; }
.bill-card-source :deep(.q-btn__content) { justify-content: flex-start; text-align: left; white-space: normal; }
.bill-card-total-row { font-weight: var(--sid-font-weight-semibold); }
.bill-card-total-row--closing { border-top: 1px solid var(--sid-color-line-strong); }
.bill-card-no-data { width: 100%; padding: var(--sid-space-5); color: var(--sid-color-text-secondary); text-align: center; }
</style>
